import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { adminApi } from "../api/admin";
import type {
  CampaignBalanceMonsterOption,
  CampaignBalanceStatBlock,
  CampaignBattleBalancePayload,
  CampaignBattleEnemyRow,
  CampaignRun,
} from "../types";

type BalanceDone =
  | { kind: "skip" }
  | { kind: "applied"; message: string; run: CampaignRun };

type EnemySlotDraft = {
  code: string | null;
  cloneFromCode: string | null;
  name: string;
  hp: number;
  atk: number;
  def_: number;
  spd: number;
  fixed_damage: number;
  formation: string;
  hero_class: string;
};

function clampStat(val: number, base: number) {
  const min = Math.max(1, Math.floor(base * 0.5));
  const max = Math.max(min + 1, Math.ceil(base * 1.5));
  return Math.min(max, Math.max(min, val));
}

function rowToDraft(row: CampaignBattleEnemyRow): EnemySlotDraft {
  return {
    code: row.code,
    cloneFromCode: null,
    name: row.name,
    hp: row.hp,
    atk: row.atk,
    def_: row.def_,
    spd: row.spd,
    fixed_damage: row.fixed_damage ?? 0,
    formation: row.formation,
    hero_class: row.hero_class,
  };
}

function monsterToDraft(m: CampaignBalanceMonsterOption): EnemySlotDraft {
  return {
    code: m.code,
    cloneFromCode: null,
    name: m.name,
    hp: m.hp,
    atk: m.atk,
    def_: m.def_,
    spd: m.spd,
    fixed_damage: m.fixed_damage,
    formation: m.formation,
    hero_class: m.hero_class,
  };
}

function StatSlider({
  label,
  value,
  base,
  onChange,
}: {
  label: string;
  value: number;
  base: number;
  onChange: (v: number) => void;
}) {
  const min = Math.max(1, Math.floor(base * 0.5));
  const max = Math.max(min + 1, Math.ceil(base * 1.5));
  return (
    <label className="block space-y-0.5">
      <div className="flex justify-between text-[10px] text-gray-400 uppercase tracking-wide">
        <span>{label}</span>
        <span className="tabular-nums text-gray-300">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(clampStat(Number(e.target.value), base))}
        className="w-full accent-emerald-500"
      />
    </label>
  );
}

function FixedDamageSlider({
  value,
  base,
  onChange,
}: {
  value: number;
  base: number;
  onChange: (v: number) => void;
}) {
  const max = Math.max(10, base + 20);
  return (
    <label className="block space-y-0.5">
      <div className="flex justify-between text-[10px] text-gray-400 uppercase tracking-wide">
        <span>Fixed dmg</span>
        <span className="tabular-nums text-gray-300">{value}</span>
      </div>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(Math.min(max, Math.max(0, Number(e.target.value))))}
        className="w-full accent-amber-500"
      />
    </label>
  );
}

export function CampaignBalanceModal({
  balance,
  runId,
  onDone,
}: {
  balance: CampaignBattleBalancePayload;
  runId: number;
  onDone: (result: BalanceDone) => void;
}) {
  const initialDraft = useMemo(() => {
    const d: Record<string, CampaignBalanceStatBlock> = {};
    for (const row of balance.suggestions) {
      d[row.code] = {
        ...row.current,
        fixed_damage: row.current.fixed_damage ?? 0,
      };
    }
    return d;
  }, [balance.suggestions]);

  const [draft, setDraft] = useState(initialDraft);
  const [slots, setSlots] = useState<EnemySlotDraft[]>(() => balance.enemies.map(rowToDraft));
  const [catalog, setCatalog] = useState<CampaignBalanceMonsterOption[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [patchGlobalTemplates, setPatchGlobalTemplates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await adminApi.campaignBalanceListMonsters(balance.stage_tier ?? null);
        if (!cancelled) setCatalog(res.monsters);
      } catch (e) {
        if (!cancelled) {
          setCatalogError(e instanceof Error ? e.message : "Failed to load monster catalog");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [balance.stage_tier]);

  const uniqueCodes = useMemo(
    () => [...new Set(balance.suggestions.map((s) => s.code))],
    [balance.suggestions],
  );

  const applySuggest = useCallback(() => {
    setDraft((prev) => {
      const next = { ...prev };
      for (const row of balance.suggestions) {
        next[row.code] = {
          ...row.suggested,
          fixed_damage: row.suggested.fixed_damage ?? 0,
        };
      }
      return next;
    });
    setMessage("Suggested values applied to sliders.");
  }, [balance.suggestions]);

  const updateStat = (code: string, field: keyof CampaignBalanceStatBlock, val: number) => {
    setDraft((prev) => {
      const meta = balance.suggestions.find((s) => s.code === code);
      const base =
        field === "fixed_damage"
          ? meta?.current.fixed_damage ?? 0
          : (meta?.current[field] as number | undefined) ?? val;
      const nextVal =
        field === "fixed_damage"
          ? Math.max(0, val)
          : clampStat(val, base as number);
      return {
        ...prev,
        [code]: {
          ...prev[code],
          [field]: nextVal,
        },
      };
    });
  };

  const removeSlot = (index: number) => {
    setSlots((rows) => rows.filter((_, i) => i !== index));
  };

  const duplicateSlot = (index: number) => {
    setSlots((rows) => {
      const src = rows[index];
      const sourceCode = src.code ?? src.cloneFromCode;
      if (!sourceCode) return rows;
      const copy: EnemySlotDraft = {
        ...src,
        code: null,
        cloneFromCode: sourceCode,
        name: `${src.name} (copy)`,
      };
      const next = [...rows];
      next.splice(index + 1, 0, copy);
      return next;
    });
  };

  const replaceSlotMonster = (index: number, code: string) => {
    const pick = catalog.find((m) => m.code === code);
    if (!pick) return;
    setSlots((rows) => {
      const next = [...rows];
      next[index] = monsterToDraft(pick);
      return next;
    });
  };

  const addSlotFromCatalog = (code: string) => {
    const pick = catalog.find((m) => m.code === code);
    if (!pick) return;
    setSlots((rows) => [...rows, monsterToDraft(pick)]);
  };

  const slotLabel = (s: EnemySlotDraft) => {
    if (s.cloneFromCode) return `${s.name} → clone from ${s.cloneFromCode}`;
    return s.code ? `${s.name} (${s.code})` : s.name;
  };

  const handleApplyRun = async () => {
    if (slots.length < 1) {
      setError("Encounter needs at least one enemy.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const enemies = slots.map((s) => ({
        code: s.cloneFromCode ? null : s.code,
        clone_from_code: s.cloneFromCode ?? undefined,
        name: s.name,
        hp: s.hp,
        atk: s.atk,
        def_: s.def_,
        spd: s.spd,
        fixed_damage: s.fixed_damage,
        formation: s.formation,
        hero_class: s.hero_class,
      }));
      const template_patches = patchGlobalTemplates
        ? uniqueCodes.map((code) => ({
            code,
            ...draft[code],
            fixed_damage: draft[code]?.fixed_damage ?? 0,
          }))
        : [];
      const res = await adminApi.campaignBalanceApplyRun({
        run_id: runId,
        node_id: balance.node_id,
        enemies,
        template_patches,
        rollback_node: true,
      });
      onDone({ kind: "applied", message: res.message, run: res.run });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Apply failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 p-2 sm:p-4">
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-kingdom-mid bg-kingdom-dark shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-balance-title"
      >
        <div className="sticky top-0 z-10 border-b border-kingdom-mid bg-kingdom-purple/95 px-4 py-3">
          <h2 id="campaign-balance-title" className="text-lg font-bold text-gold-300 font-game">
            Campaign balance
          </h2>
          <p className="text-xs text-gray-400 mt-1">{balance.summary}</p>
        </div>

        <div className="px-4 py-3 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-lg bg-kingdom-purple/60 py-2">
              <div className="text-gray-500">Turns</div>
              <div className="font-bold text-white">{balance.metrics.turn_count}</div>
            </div>
            <div className="rounded-lg bg-kingdom-purple/60 py-2">
              <div className="text-gray-500">HP left</div>
              <div className="font-bold text-white">{balance.metrics.player_hp_pct_end}%</div>
            </div>
            <div className="rounded-lg bg-kingdom-purple/60 py-2">
              <div className="text-gray-500">Elite ×</div>
              <div className="font-bold text-white">{balance.elite_multiplier}</div>
            </div>
          </div>

          {balance.recommendation_notes.length > 0 && (
            <ul className="text-xs text-amber-200/90 list-disc pl-4 space-y-1">
              {balance.recommendation_notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-white">Encounter (this node)</span>
              <span className="text-[10px] text-gray-500">{slots.length} mobs</span>
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {slots.map((row, idx) => (
                <div
                  key={`slot-${idx}-${row.code ?? row.cloneFromCode ?? "x"}`}
                  className="flex flex-col gap-1 text-xs bg-kingdom-purple/40 rounded-lg px-2 py-1.5"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex-1 truncate text-gray-200">{slotLabel(row)}</span>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-white px-1"
                      title="Duplicate (new row in monster_templates)"
                      onClick={() => duplicateSlot(idx)}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-rose-400 px-1 disabled:opacity-30"
                      disabled={slots.length <= 1}
                      title="Remove"
                      onClick={() => removeSlot(idx)}
                    >
                      −
                    </button>
                  </div>
                  {catalog.length > 0 && (
                    <select
                      className="w-full text-[10px] bg-kingdom-dark border border-kingdom-mid rounded px-1 py-0.5 text-gray-300"
                      value={row.cloneFromCode ? "" : row.code ?? ""}
                      onChange={(e) => {
                        if (e.target.value) replaceSlotMonster(idx, e.target.value);
                      }}
                    >
                      <option value="">{row.cloneFromCode ? "Pending clone…" : "Replace with…"}</option>
                      {catalog.map((m) => (
                        <option key={m.code} value={m.code}>
                          {m.name} ({m.code})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
            {catalog.length > 0 && (
              <select
                className="mt-2 w-full text-xs bg-kingdom-dark border border-kingdom-mid rounded-lg px-2 py-1.5 text-gray-300"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    addSlotFromCatalog(e.target.value);
                    e.target.value = "";
                  }
                }}
              >
                <option value="">+ Add mob from catalog…</option>
                {catalog.map((m) => (
                  <option key={`add-${m.code}`} value={m.code}>
                    {m.name} ({m.code})
                  </option>
                ))}
              </select>
            )}
            {catalogError && <p className="text-[10px] text-amber-400 mt-1">{catalogError}</p>}
            <p className="text-[10px] text-gray-500 mt-1">
              Duplicate creates a new monster_templates row (e.g. brute_t1_2) — available in all dungeons.
              Apply run saves this node and reopens it on the map (no reward rollback).
            </p>
          </div>

          <button
            type="button"
            onClick={applySuggest}
            className="w-full py-2 rounded-lg border border-emerald-600/50 text-emerald-300 text-sm font-semibold hover:bg-emerald-950/40"
          >
            Apply auto-suggest to sliders
          </button>

          <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={patchGlobalTemplates}
              onChange={(e) => setPatchGlobalTemplates(e.target.checked)}
              className="mt-0.5 rounded border-kingdom-mid"
            />
            <span>Also patch global monster_templates from stat sliders (for codes in this fight)</span>
          </label>

          {uniqueCodes.map((code) => {
            const meta = balance.suggestions.find((s) => s.code === code);
            if (!meta) return null;
            const stats = draft[code] ?? meta.current;
            const fdBase = meta.current.fixed_damage ?? 0;
            return (
              <div key={code} className="rounded-xl border border-kingdom-mid p-3 space-y-2">
                <div className="font-semibold text-sm text-white">{meta.name}</div>
                <div className="text-[10px] text-gray-500 font-mono">{code}</div>
                <StatSlider
                  label="HP"
                  value={stats.hp}
                  base={meta.current.hp}
                  onChange={(v) => updateStat(code, "hp", v)}
                />
                <StatSlider
                  label="ATK"
                  value={stats.atk}
                  base={meta.current.atk}
                  onChange={(v) => updateStat(code, "atk", v)}
                />
                <StatSlider
                  label="DEF"
                  value={stats.def_}
                  base={meta.current.def_}
                  onChange={(v) => updateStat(code, "def_", v)}
                />
                <StatSlider
                  label="SPD"
                  value={stats.spd}
                  base={meta.current.spd}
                  onChange={(v) => updateStat(code, "spd", v)}
                />
                <FixedDamageSlider
                  value={stats.fixed_damage ?? 0}
                  base={fdBase}
                  onChange={(v) => updateStat(code, "fixed_damage", v)}
                />
              </div>
            );
          })}

          {message && <p className="text-xs text-emerald-400">{message}</p>}
          {error && <p className="text-xs text-rose-400">{error}</p>}
        </div>

        <div className="sticky bottom-0 flex gap-2 border-t border-kingdom-mid bg-kingdom-dark px-4 py-3">
          <button
            type="button"
            disabled={loading}
            onClick={() => onDone({ kind: "skip" })}
            className="flex-1 py-2.5 rounded-lg border border-kingdom-mid text-gray-300 text-sm font-semibold"
          >
            Skip
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => void handleApplyRun()}
            className={clsx(
              "flex-1 py-2.5 rounded-lg text-sm font-semibold",
              loading ? "bg-gray-700 text-gray-400" : "bg-emerald-700 text-white hover:bg-emerald-600",
            )}
          >
            {loading ? "Saving…" : "Apply to run"}
          </button>
        </div>
      </div>
    </div>
  );
}
